/**
 * Exit Intent Detection for Cart Abandonment
 * 
 * Detects when user attempts to leave the site (mouse exits viewport at top)
 * and shows a popup if cart has items. Respects user preferences and session limits.
 */

(function() {
  'use strict';

  const CONFIG = {
    sessionStorageKey: 'exitIntentShown',
    localStorageKey: 'exitIntentDismissed',
    mouseLeaveSensitivity: 10, // pixels from top where exit is detected
    mouseLeaveDelay: 200, // ms delay to prevent false positives
    analyticsCategory: 'Exit Intent',
  };

  let exitIntentTriggered = false;
  let mouseLeaveTimer = null;

  /**
   * Check if device is mobile
   */
  function isMobile() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) 
      || window.innerWidth < 768;
  }

  /**
   * Check if cart has items
   */
  async function hasCartItems() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      return cart.item_count > 0;
    } catch (error) {
      console.error('Exit intent: Error checking cart:', error);
      return false;
    }
  }

  /**
   * Check if popup should be shown
   */
  function shouldShowPopup() {
    // Don't show on mobile
    if (isMobile()) {
      return false;
    }

    // Check if already shown this session
    if (sessionStorage.getItem(CONFIG.sessionStorageKey)) {
      return false;
    }

    // Check if user has dismissed permanently
    if (localStorage.getItem(CONFIG.localStorageKey)) {
      return false;
    }

    return true;
  }

  /**
   * Show the exit intent popup
   */
  async function showPopup() {
    if (exitIntentTriggered || !shouldShowPopup()) {
      return;
    }

    // Check if cart has items
    const hasItems = await hasCartItems();
    if (!hasItems) {
      return;
    }

    exitIntentTriggered = true;

    // Mark as shown in session
    sessionStorage.setItem(CONFIG.sessionStorageKey, 'true');

    // Get popup element
    const popup = document.getElementById('exit-intent-popup');
    if (!popup) {
      console.error('Exit intent: Popup element not found');
      return;
    }

    // Show popup
    popup.classList.add('active');
    document.body.style.overflow = 'hidden';

    // Track impression
    trackEvent('Impression', 'Popup Shown');

    // Focus on popup for accessibility
    const closeButton = popup.querySelector('.exit-intent-popup__close');
    if (closeButton) {
      closeButton.focus();
    }
  }

  /**
   * Hide the popup
   */
  function hidePopup(dismissPermanently = false) {
    const popup = document.getElementById('exit-intent-popup');
    if (!popup) {
      return;
    }

    popup.classList.remove('active');
    document.body.style.overflow = '';

    if (dismissPermanently) {
      localStorage.setItem(CONFIG.localStorageKey, 'true');
      trackEvent('Dismissed', 'Permanent Dismissal');
    } else {
      trackEvent('Dismissed', 'Session Dismissal');
    }
  }

  /**
   * Handle mouse leave event
   */
  function handleMouseLeave(event) {
    // Only trigger if mouse leaves from top of viewport
    if (event.clientY > CONFIG.mouseLeaveSensitivity) {
      return;
    }

    // Clear any existing timer
    if (mouseLeaveTimer) {
      clearTimeout(mouseLeaveTimer);
    }

    // Set a delay to prevent false positives from rapid mouse movements
    mouseLeaveTimer = setTimeout(() => {
      showPopup();
    }, CONFIG.mouseLeaveDelay);
  }

  /**
   * Handle mouse enter event (cancel pending popup)
   */
  function handleMouseEnter() {
    if (mouseLeaveTimer) {
      clearTimeout(mouseLeaveTimer);
      mouseLeaveTimer = null;
    }
  }

  /**
   * Track analytics event
   */
  function trackEvent(action, label) {
    // Google Analytics (gtag.js)
    if (typeof gtag !== 'undefined') {
      gtag('event', action, {
        event_category: CONFIG.analyticsCategory,
        event_label: label,
      });
    }

    // Google Analytics (Universal Analytics)
    if (typeof ga !== 'undefined') {
      ga('send', 'event', CONFIG.analyticsCategory, action, label);
    }

    // Shopify Analytics
    if (typeof window.ShopifyAnalytics !== 'undefined') {
      window.ShopifyAnalytics.lib.track('Exit Intent: ' + action, {
        label: label,
      });
    }

    // Console log for debugging
    console.log('Exit Intent Analytics:', action, label);
  }

  /**
   * Initialize exit intent detection
   */
  function init() {
    // Skip on mobile
    if (isMobile()) {
      return;
    }

    // Skip if already shown this session or dismissed
    if (!shouldShowPopup()) {
      return;
    }

    // Add mouse leave listener
    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mouseenter', handleMouseEnter);

    // Setup popup event listeners
    const popup = document.getElementById('exit-intent-popup');
    if (!popup) {
      return;
    }

    // Close button
    const closeButton = popup.querySelector('.exit-intent-popup__close');
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        hidePopup(false);
      });
    }

    // Overlay click (close popup)
    const overlay = popup.querySelector('.exit-intent-popup__overlay');
    if (overlay) {
      overlay.addEventListener('click', () => {
        hidePopup(false);
      });
    }

    // CTA button tracking
    const ctaButton = popup.querySelector('.exit-intent-popup__cta');
    if (ctaButton) {
      ctaButton.addEventListener('click', () => {
        trackEvent('CTA Click', 'Return to Cart');
        hidePopup(false);
      });
    }

    // Dismiss button (permanent dismissal)
    const dismissButton = popup.querySelector('[data-exit-intent-dismiss]');
    if (dismissButton) {
      dismissButton.addEventListener('click', () => {
        hidePopup(true);
      });
    }

    // Keyboard handling
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && popup.classList.contains('active')) {
        hidePopup(false);
      }
    });

    // Handle window resize (check if became mobile)
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (isMobile() && popup.classList.contains('active')) {
          hidePopup(false);
        }
      }, 250);
    });

    console.log('Exit intent detection initialized');
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose API for testing/debugging
  window.exitIntent = {
    show: showPopup,
    hide: hidePopup,
    reset: function() {
      sessionStorage.removeItem(CONFIG.sessionStorageKey);
      localStorage.removeItem(CONFIG.localStorageKey);
      exitIntentTriggered = false;
    }
  };
})();
